/**
 * Time utilities — always display in Asia/Kolkata (IST, UTC+5:30)
 * Uses the browser's built-in Intl API — no extra packages needed.
 */

const TZ = 'Asia/Kolkata';

/**
 * Parse any date value safely into a Date object.
 * SQLite stores datetime as "YYYY-MM-DD HH:MM:SS" (no T, no Z) — treat as UTC.
 */
export function parseDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  const s = String(val).trim();
  // SQLite format "2026-09-17 17:14:55" — append Z so JS treats it as UTC
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) {
    return new Date(s.replace(' ', 'T') + 'Z');
  }
  // ISO format without Z "2026-09-17T17:14:55" — append Z so JS treats as UTC
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !s.endsWith('Z')) {
    return new Date(s + 'Z');
  }
  // Date-only format "2026-10-31" — treat as UTC date
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s + 'T00:00:00Z');
  }
  return new Date(s);
}

/**
 * Format a date value in IST using Intl.DateTimeFormat.
 * @param {string|Date} val - date value
 * @param {string} fmt - format string matching common date-fns patterns
 *   Supported tokens: dd, MM, yyyy, yy, HH, mm, ss, MMM, MMMM, EEE, EEEE, d
 * @returns {string}
 */
export function formatIST(val, fmt) {
  const d = parseDate(val);
  if (!d || isNaN(d.getTime())) return String(val || '—');

  // Get IST parts via Intl
  const parts = new Intl.DateTimeFormat('en-IN', {
    timeZone: TZ,
    year:   'numeric',
    month:  '2-digit',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    weekday: 'long',
  }).formatToParts(d);

  const get = (type) => parts.find(p => p.type === type)?.value || '';

  const year    = get('year');
  const month   = get('month');
  const day     = get('day');
  const hour    = get('hour') === '24' ? '00' : get('hour');
  const minute  = get('minute');
  const second  = get('second');
  const weekday = get('weekday'); // e.g. "Monday"

  // Month name lookups
  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const MONTHS_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const monthIdx = parseInt(month, 10) - 1;

  // Weekday short
  const weekdayShort = weekday.slice(0, 3); // "Mon", "Tue" etc.

  return fmt
    .replace('EEEE', weekday)
    .replace('EEE',  weekdayShort)
    .replace('MMMM', MONTHS_LONG[monthIdx])
    .replace('MMM',  MONTHS_SHORT[monthIdx])
    .replace('yyyy', year)
    .replace('yy',   year.slice(-2))
    .replace('MM',   month)
    .replace('dd',   day)
    .replace('d',    String(parseInt(day, 10)))
    .replace('HH',   hour)
    .replace('mm',   minute)
    .replace('ss',   second);
}

/**
 * Shorthand helpers matching common patterns used across the app
 */
export const fmtDateTime  = (v) => formatIST(v, 'dd MMM yyyy, HH:mm');
export const fmtDate      = (v) => formatIST(v, 'dd MMM yyyy');
export const fmtTime      = (v) => formatIST(v, 'HH:mm');
export const fmtTimeSec   = (v) => formatIST(v, 'HH:mm:ss');
export const fmtDateShort = (v) => formatIST(v, 'dd MMM, HH:mm');
export const fmtFull      = (v) => formatIST(v, 'EEEE, d MMMM yyyy');
export const fmtDayDate   = (v) => formatIST(v, 'EEE, MMM d yyyy');
export const fmtMd        = (v) => formatIST(v, 'MMM d');
export const fmtMdHm      = (v) => formatIST(v, 'MMM d, HH:mm');
export const fmtDdMmYyyy  = (v) => formatIST(v, 'dd/MM/yyyy');
export const fmtIsoDate   = (v) => formatIST(v, 'yyyy-MM-dd');

/**
 * Get today's date in IST as YYYY-MM-DD string
 */
export function todayIST() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year:'numeric', month:'2-digit', day:'2-digit' }).format(new Date());
}

/**
 * Get N days ago from today in IST as YYYY-MM-DD string
 */
export function daysAgoIST(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year:'numeric', month:'2-digit', day:'2-digit' }).format(d);
}
